<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: [String, Number], required: true },
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  enabled: { type: Boolean, default: undefined },
  icon: { type: String, default: '' },
  routeName: { type: String, default: '' },
});
const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;
const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const hasKnownStatus = computed(() => typeof props.enabled === 'boolean');
const integrationStatus = computed(() => {
  if (!hasKnownStatus.value) return t('INTEGRATION_APPS.STATUS.AVAILABLE');
  return props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED');
});
const statusClasses = computed(() => {
  if (!hasKnownStatus.value) return 'bg-n-blue-3 text-n-blue-text';
  return props.enabled
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-slate-3 text-n-slate-10';
});
const actionURL = computed(() =>
  props.routeName
    ? { name: props.routeName }
    : frontendURL(
        `accounts/${accountId.value}/settings/integrations/${props.id}`
      )
);
</script>

<template>
  <article
    class="group flex min-h-56 flex-col rounded-xl border border-n-weak bg-n-alpha-2 p-5 transition hover:-translate-y-0.5 hover:border-n-strong hover:shadow-sm"
  >
    <div class="flex items-start justify-between gap-3">
      <div
        class="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1 shadow-sm"
      >
        <i v-if="icon" class="size-6 text-n-slate-11" :class="[icon]" />
        <template v-else>
          <img
            :src="`/dashboard/images/integrations/${id}.png`"
            :alt="name"
            class="size-full object-contain p-1.5 dark:hidden"
          />
          <img
            :src="`/dashboard/images/integrations/${id}-dark.png`"
            :alt="name"
            class="hidden size-full object-contain p-1.5 dark:block"
          />
        </template>
      </div>
      <span
        class="rounded-full px-2.5 py-1 text-xs font-medium"
        :class="statusClasses"
      >
        {{ integrationStatus }}
      </span>
    </div>
    <div class="mt-4 flex flex-1 flex-col">
      <h2 class="m-0 text-base font-semibold text-n-slate-12">{{ name }}</h2>
      <p class="mb-4 mt-1.5 line-clamp-3 text-sm leading-5 text-n-slate-10">
        {{ replaceInstallationName(description) }}
      </p>
      <router-link :to="actionURL" class="mt-auto w-fit">
        <Button
          :label="$t('INTEGRATION_APPS.CONFIGURE')"
          icon="i-lucide-arrow-right"
          trailing-icon
          link
        />
      </router-link>
    </div>
  </article>
</template>
